/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useState, type FC, type FormEventHandler } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { Button, Heading, Text } from "@vector-im/compound-web";
import { useNavigate } from "react-router-dom";

import { useGroupCallRooms } from "./useGroupCallRooms";
import { Header, HeaderLogo, LeftNav, RightNav } from "../Header";
import commonStyles from "./common.module.css";
import styles from "./RegisteredView.module.css";
import { CallList } from "./CallList";
import { UserMenuContainer } from "../UserMenuContainer";
import { AnalyticsNotice } from "../analytics/AnalyticsNotice";
import { useOptInAnalytics } from "../settings/settings";
import { useUrlParams } from "../UrlParams";
import { Form } from "../form/Form";
import { ErrorMessage, FieldRow, InputField } from "../input/Input";

interface Props {
  client: MatrixClient;
}

export const RegisteredView: FC<Props> = ({ client }) => {
  const { header } = useUrlParams();
  const [optInAnalytics] = useOptInAnalytics();
  const [error, setError] = useState<Error>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const recentRooms = useGroupCallRooms(client);
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      setError(undefined);

      const data = new FormData(e.target as HTMLFormElement);
      const meetingLink = data.get("meetingLink");
      const meetingPath =
        typeof meetingLink === "string"
          ? pathFromMeetingLink(meetingLink)
          : undefined;

      if (!meetingPath) {
        setError(new Error("Enter a valid meeting link."));
        return;
      }

      navigate(meetingPath)?.catch(setError);
    },
    [navigate],
  );

  return (
    <div className={commonStyles.container}>
      {header === "standard" && (
        <Header>
          <LeftNav>
            <HeaderLogo />
          </LeftNav>
          <RightNav>
            <UserMenuContainer />
          </RightNav>
        </Header>
      )}
      <main className={commonStyles.main}>
        <HeaderLogo className={commonStyles.logo} />
        <Heading size="lg" weight="semibold" className={styles.heading}>
          {t("common.home")}
        </Heading>
        {optInAnalytics === null && (
          <Text size="sm" className={styles.notice}>
            <AnalyticsNotice />
          </Text>
        )}
        <Form className={styles.form} onSubmit={onSubmit}>
          <FieldRow className={styles.fieldRow}>
            <InputField
              id="meetingLink"
              name="meetingLink"
              label="Meeting link"
              placeholder="Paste meeting link"
              type="text"
              required
              autoComplete="off"
              data-testid="home_meetingLink"
            />

            <Button
              type="submit"
              size="lg"
              className={styles.button}
              data-testid="home_joinMeeting"
            >
              Join
            </Button>
          </FieldRow>
          {error && (
            <FieldRow className={styles.fieldRow}>
              <ErrorMessage error={error} />
            </FieldRow>
          )}
        </Form>
        {recentRooms.length > 0 ? (
          <CallList rooms={recentRooms} client={client} />
        ) : (
          <Text size="md" className={styles.emptyState}>
            Paste a meeting invitation link to join a call.
          </Text>
        )}
      </main>
    </div>
  );
};

function pathFromMeetingLink(rawLink: string): string | undefined {
  const link = rawLink.trim();
  if (!link) return undefined;

  try {
    const url = new URL(
      link.startsWith("/") || link.startsWith("http")
        ? link
        : `https://${link}`,
      window.location.origin,
    );
    const fragmentQuery = url.hash.split("?")[1] ?? "";
    const hasRoomID =
      url.searchParams.has("roomId") ||
      new URLSearchParams(fragmentQuery).has("roomId");

    if (!hasRoomID || !url.pathname.startsWith("/room")) {
      return undefined;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}
